import { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle2, AlertTriangle, Info } from 'lucide-react';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastOptions {
  action?: ToastAction;
  duration?: number;
}

interface ToastItem {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
  action?: ToastAction;
  duration: number;
}

let toastId = 0;
let addToastFn: ((message: string, type: ToastItem['type'], options?: ToastOptions) => void) | null = null;

export function toast(message: string, type: ToastItem['type'] = 'info', options?: ToastOptions) {
  addToastFn?.(message, type, options);
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((message: string, type: ToastItem['type'], options?: ToastOptions) => {
    const id = ++toastId;
    const duration = options?.duration || 4000;
    setToasts(prev => [...prev, { id, message, type, action: options?.action, duration }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  useEffect(() => {
    addToastFn = addToast;
    return () => { addToastFn = null; };
  }, [addToast]);

  const remove = (id: number) => setToasts(prev => prev.filter(t => t.id !== id));

  const icons = { success: CheckCircle2, error: AlertTriangle, info: Info };
  const colors = {
    success: 'border-[var(--success)] bg-[var(--success)]/10',
    error: 'border-[var(--danger)] bg-[var(--danger)]/10',
    info: 'border-[var(--info)] bg-[var(--info)]/10',
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] space-y-2 max-w-sm">
      {toasts.map(t => {
        const Icon = icons[t.type];
        return (
          <div key={t.id} className={`flex items-start gap-2 px-4 py-3 rounded-lg border shadow-surface-md animate-slide-up ${colors[t.type]}`}>
            <Icon className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm">{t.message}</p>
              {t.action && (
                <button
                  onClick={() => { t.action!.onClick(); remove(t.id); }}
                  className="text-xs font-medium accent hover:underline mt-1"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button onClick={() => remove(t.id)} className="text-dim hover:text-muted shrink-0" aria-label="Schliessen"><X className="w-3.5 h-3.5" /></button>
          </div>
        );
      })}
    </div>
  );
}
